import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers';
import { Checkbox, FormControl, InputLabel, MenuItem, Select } from '@mui/material';

type Props = {
    disabled: boolean;
    onReadyToSubmit: (formData: FormData | null, isValid: boolean) => void;
    kind: string
    reset: boolean
  };
  

const CustomForm = ({ kind, disabled, onReadyToSubmit, reset }: Props) => {
    const [companyName, setCompanyName] = useState('');
    const [companyStartDate, setCompanyStartDate] = useState<Date | undefined>(undefined);
    const [piva, setPiva] = useState('');
    const [cf, setCf] = useState('');
    const [cup, setCup] = useState('');
    const [atecoRequest, setAtecoRequest] = useState('');
    const [atecoCurrent, setAtecoCurrent] = useState('');
    const [nRequest, setNRequest] = useState('');
    const [totalRequestAllowed, setTotalRequestAllowed] = useState<number | undefined>(undefined);
    const [tranche, setTranche] = useState<'unique' | 'first' | 'second' | 'third' | 'payoff'>('unique');
    const [incentiveMeasure, setIncentiveMeasure] = useState<number>(70);
    const [requestDate, setRequestDate] = useState<Date | undefined>(undefined);
    const [endDate, setEndDate] =  useState<Date | undefined>(undefined);
    const [concessionDate, setConcessionDate] =  useState<Date | undefined>(undefined);
    const [cupCommunicationDate, setCupCommunicationDate] =  useState<Date | undefined>(undefined);
    
    const [territorialImpact, setTerritorialImpact] = useState<boolean>(false)
    
    const [zipFile, setZipFile] = useState<File | null>(null);
    const [excelFile, setExcelFile] = useState<File | null>(null);
    const [edited, setEdited] = useState<Record<string, boolean>>({});
    const [errorMessages, setErrorMessages] = useState<Record<string, string>>({})
    
    const handleEdit = (field: string) => setEdited(prev => ({ ...prev, [field]: true }));
    
    const isPiva = (v: string) => /^\d{11}$/.test(v);
    const isCf = (v: string) => /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/i.test(v);
    const isCup = (v: string) => /^[A-Z]\d{2}[A-Z]\d{11}$/.test(v);
    const isAteco = (v: string) => /^\d{2}\.\d{2}\.\d{2}$/.test(v);
  
    const validators = {
      companyName: () => (!companyName.trim() ? 'Nome azienda obbligatorio' : null),
      piva: () => (!isPiva(piva) ? 'La partita IVA deve avere 11 cifre' : null),
      cf: () => {
        if (kind === 'fesr') return null
        if (!cf.trim()) return 'Inserire Partita IVA o Codice Fiscale';
        if (!isPiva(cf) && !isCf(cf))
          return 'Inserire una Partita IVA (11 cifre) o un Codice Fiscale valido (16 caratteri)';
        return null;
      },
      cup: () => (!cup.trim() ? 'CUP obbligatorio' : !isCup(cup) ? 'CUP non valido' : null),
      companyStartDate: () => (!companyStartDate ? 'Data costituzione obbligatoria' : null),
      requestDate: () => {
        if (!requestDate) return 'Data richiesta obbligatoria';
        if (companyStartDate && requestDate <= companyStartDate)
          return 'La data richiesta deve essere successiva alla data di costituzione';
        return null;
      },
      endDate: () => ((kind === 'fesr' && !endDate) ? 'Data fine progetto obbligatoria' : null),
      concessionDate: () => ((kind === 'fesr' && !concessionDate) ? 'Data comunicazione concessione obbligatoria': null),
      cupCommunicationDate: () => ((kind !== 'fesr' && !cupCommunicationDate) ? 'Data comunicazione CUP obbligatoria': null),
      atecoRequest: () =>
        !atecoRequest.trim() ? 'Codice ATECO domanda obbligatorio' : !isAteco(atecoRequest) ? 'Codice ATECO non valido' : null,
      atecoCurrent: () =>
        !atecoCurrent.trim() ? 'Codice ATECO attuale obbligatorio' : !isAteco(atecoCurrent) ? 'Codice ATECO non valido' : null,
      totalRequestAllowed: () =>
        totalRequestAllowed === undefined || totalRequestAllowed <= 0 ? 'Spesa totale ammessa obbligatoria e maggiore di 0' : null,
      zipFile: () => (!zipFile ? 'File ZIP obbligatorio' : null),
      excelFile: () => (kind === 'fesr' && !excelFile ? 'File Excel/CSV obbligatorio' : null),
    } as const;
  
    useEffect(() => {
      const newErrors: Record<string, string> = {};
      (Object.keys(validators) as Array<keyof typeof validators>).forEach((k) => {
        if (edited[k]) {
          const err = validators[k]();
          if (err) newErrors[k] = err;
        }
      });
  
      setErrorMessages((prev) => {
        const prevKeys = Object.keys(prev).sort();
        const nextKeys = Object.keys(newErrors).sort();
        if (prevKeys.length === nextKeys.length && prevKeys.every((v, i) => v === nextKeys[i] && prev[v] === newErrors[v])) {
          return prev;
        }
        return newErrors;
      });
    }, [
      edited,
      companyName,
      companyStartDate,
      piva,
      cf,
      cup,
      atecoRequest,
      atecoCurrent,
      totalRequestAllowed,
      zipFile,
      excelFile,
      requestDate,
      endDate,
      concessionDate,
      cupCommunicationDate,
      kind,
    ]);

    useEffect(() => {
      if (reset) {
        setCompanyName('')
        setPiva('')
        setCf('')
        setCup('')
        setAtecoRequest('')
        setAtecoCurrent('')
        setNRequest('')
        setTotalRequestAllowed(undefined)
        setTranche('unique')
        setIncentiveMeasure(70)
        setRequestDate(undefined)
        setEndDate(undefined)
        setConcessionDate(undefined)
        setCupCommunicationDate(undefined)
        setTerritorialImpact(false)
        setZipFile(null)
        setExcelFile(null)
        setEdited({})
        setErrorMessages({})
      }
    }, [reset]);
  
    const areAllRequiredFieldsValid = useCallback(() => {
      return (Object.keys(validators) as Array<keyof typeof validators>).every((k) => {
        const err = validators[k]();
        return err === null;
      });
    }, [
      companyName,
      companyStartDate,
      piva,
      cf,
      cup,
      atecoRequest,
      atecoCurrent,
      totalRequestAllowed,
      zipFile,
      excelFile,
      requestDate,
      endDate,
      concessionDate,
      cupCommunicationDate,
      kind,
    ]);

    useEffect(() => {
        const hasAnyEdited = Object.values(edited).some(Boolean);
        if (!hasAnyEdited) {
          onReadyToSubmit(null, false);
          return;
        }
    
        if (!areAllRequiredFieldsValid()) {
          onReadyToSubmit(null, false);
          return;
        }
    
        const formData = new FormData();
        formData.append('kind', kind ?? '');
        if (companyName) formData.append('company_name', companyName);
        if (companyStartDate) formData.append('company_start_date', dayjs(companyStartDate).format('YYYY-MM-DD'));
        if (piva) formData.append('piva', piva);
        if (kind !== 'fesr' && cf) formData.append('cf', cf);
        if (cup) formData.append('cup', cup);
        if (atecoRequest) formData.append('ateco_request', atecoRequest);
        if (atecoCurrent) formData.append('ateco_current', atecoCurrent);
        if (nRequest) formData.append('n_request', nRequest);
        if (totalRequestAllowed !== undefined) formData.append('total_request_allowed', totalRequestAllowed.toString());
        formData.append('tranche', tranche);
        formData.append('incentive_measure', String(incentiveMeasure));
        formData.append('territorial_impact', String(territorialImpact))
        if (requestDate) formData.append('request_date', dayjs(requestDate).format('YYYY-MM-DD'));
        if (endDate) formData.append('end_date', dayjs(endDate).format('YYYY-MM-DD'));
        if (concessionDate) formData.append('concession_date', dayjs(concessionDate).format('YYYY-MM-DD'))
        if (cupCommunicationDate) formData.append('cup_communication_date', dayjs(cupCommunicationDate).format('YYYY-MM-DD'))
        if (zipFile) formData.append('zip_file', zipFile);
        if (kind === 'fesr' && excelFile) formData.append('export_imputations', excelFile);
    
        onReadyToSubmit(formData, true);
      }, [
        edited,
        companyName,
        companyStartDate,
        piva,
        cf,
        cup,
        atecoRequest,
        atecoCurrent,
        nRequest,
        totalRequestAllowed,
        tranche,
        incentiveMeasure,
        requestDate,
        endDate,
        concessionDate,
        cupCommunicationDate,
        zipFile,
        excelFile,
        kind,
        areAllRequiredFieldsValid,
        onReadyToSubmit,
    ]);

  const onDropZip = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setZipFile(file);
      toast.success(`File ZIP aggiunto: ${file.name}`);
    }
  };

  const onDropExcel = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setExcelFile(file);
      toast.success(`File Excel/CSV aggiunto: ${file.name}`);
    }
  };

  const formatValue = (inputValue: any) =>{
    const selectionStart = inputValue.selectionStart ?? 0;
    const raw = inputValue.value;

    const val = raw.replace(/\./g, "").replace(",", ".");
    const num = parseFloat(val);
    setTotalRequestAllowed(isNaN(num) ? undefined : num);

    const digitsBefore = (raw.slice(0, selectionStart).match(/\d/g) || []).length;

    requestAnimationFrame(() => {
    if (inputValue.setSelectionRange) {
        const formatted = inputValue.value;
        let digitCount = 0;
        let newPos = 0;

        for (let i = 0; i < formatted.length; i++) {
        if (/\d/.test(formatted.charAt(i))) digitCount++;
        if (digitCount >= digitsBefore) {
            newPos = i + 1;
            break;
        }
        }
        if (digitsBefore === 0) newPos = 0;
        if (digitCount < digitsBefore) newPos = formatted.length;
        try { inputValue.setSelectionRange(newPos, newPos); } catch {}
    }
    });
  }

  const {
    getRootProps: getZipRootProps,
    getInputProps: getZipInputProps,
    isDragActive: isZipDragActive
  } = useDropzone({
    onDrop: onDropZip,
    accept: { 'application/zip': ['.zip'] },
    maxSize: 100 * 1024 * 1024,
    multiple: false,
  });

  const {
    getRootProps: getExcelRootProps,
    getInputProps: getExcelInputProps,
    isDragActive: isExcelDragActive
  } = useDropzone({
    onDrop: onDropExcel,
    accept: {
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
    },
    maxSize: 10 * 1024 * 1024,
    multiple: false,
  })

  return (
    <>
    <div className='row'>
        <div className={`col ${ kind === 'fesr' ? 'col-lg': 'col-sm'}`}>
            <label>Nome Azienda</label>
            <input
                type="text"
                value={companyName}
                onChange={(e) => {
                    setCompanyName(e.target.value);
                    handleEdit("companyName");
                }}
                disabled={disabled}
                placeholder="Nome azienda"
            />
        </div>
        <div className='col col-sm'>
            <label>Partita IVA</label>
            <input
                type="text"
                value={piva}
                onChange={(e) => {
                    setPiva(e.target.value);
                    handleEdit("piva");
                }}
                disabled={disabled}
                placeholder="02012345678"
                className={errorMessages.piva ? "is-invalid" : ""}
            />
            {errorMessages.piva && <div className="text-danger">{errorMessages.piva}</div>}
        </div>
        { kind !== "fesr" && 
        <div className='col col-sm'>
            <label>Codice Fiscale</label>
            <input
                type="text"
                value={cf}
                onChange={(e) => {
                    setCf(e.target.value);
                    handleEdit("cf");
                }}
                disabled={disabled}
                placeholder="01234567890"
                className={errorMessages.cf ? "is-invalid" : ""}
            />
            {errorMessages.cf && <div className="text-danger">{errorMessages.cf}</div>}
        </div>

        }
    </div>

    <div className='row'>

        <div className='row' style={{width:'50%'}}>
            <div className='col col-md'>
                <label>Numero Richiesta</label>
                <input
                    type="text"
                    value={nRequest}
                    onChange={(e) => {
                        setNRequest(e.target.value);
                    }}
                    disabled={disabled}
                    placeholder="42"
                />
            </div>
            <div className='col col-md'>
                <label>CUP</label>
                <input
                    type="text"
                    value={cup}
                    onChange={(e) => {
                        setCup(e.target.value);
                        handleEdit("cup");
                    }}
                    disabled={disabled}
                    placeholder="C00A01234567890"
                    className={errorMessages.cup ? "is-invalid" : ""}  
                />
                {errorMessages.cup && <div className="text-danger">{errorMessages.cup}</div>}
            </div>
        </div>

        <div className='col col-sm'>
            <label>Data Costituzione Azienda</label>
            <DatePicker
                value={companyStartDate ? dayjs(companyStartDate) : null}
                onChange={(newValue) => {
                    setCompanyStartDate(newValue ? newValue.toDate() : undefined);
                    handleEdit("companyStartDate");
                }}
                format="DD-MM-YYYY"
                slotProps={{
                textField: {
                    required: true,
                    fullWidth: true,
                    disabled,
                },
                }}
            />
        </div>
    </div>

    <div className='row'>
        <div className='row' style={{width:'50%'}}>
            <div className='col col-md'>
                <label>Codice ATECO Domanda</label>
                <input
                    type="text"
                    value={atecoRequest}
                    onChange={(e) => {
                        setAtecoRequest(e.target.value);
                        handleEdit("atecoRequest");
                    }}
                    disabled={disabled}
                    placeholder="10.01.01"
                    className={errorMessages.atecoRequest ? "is-invalid" : ""}
                />
                {errorMessages.atecoRequest && <div className="text-danger">{errorMessages.atecoRequest}</div>}
            </div>
            <div className='col col-md'>
                <label>Codice ATECO Attuale</label>
                <input
                    type="text"
                    value={atecoCurrent}
                    onChange={(e) => {
                        setAtecoCurrent(e.target.value);
                        handleEdit("atecoCurrent");
                    }}
                    disabled={disabled}
                    placeholder="10.01.01"
                    className={errorMessages.atecoCurrent ? "is-invalid" : ""}
                />
                {errorMessages.atecoCurrent && <div className="text-danger">{errorMessages.atecoCurrent}</div>}
            </div>
        </div>
        <div className='col col-sm'>
            <label>Data Richiesta Incentivo</label>
            <DatePicker
                value={requestDate ? dayjs(requestDate) : null}
                onChange={(newValue) => {
                setRequestDate(newValue ? newValue.toDate() : undefined);
                handleEdit("requestDate");
                }}
                format="DD-MM-YYYY"
                slotProps={{
                textField: {
                    required: true,
                    fullWidth: true,
                    disabled,
                },
                }}
            />
            {errorMessages.requestDate && <div className="text-danger">{errorMessages.requestDate}</div>}
        </div>
        
    </div>

    <div className='row' style={{justifyContent: 'flex-end'}}>
        <div className='col col-sm'>
            <label>Data Fine Progetto</label>
            <DatePicker
                value={endDate ? dayjs(endDate) : null}
                onChange={(newValue) => {
                setEndDate(newValue ? newValue.toDate() : undefined);
                handleEdit("endDate");
                }}
                format="DD-MM-YYYY"
                slotProps={{
                textField: {
                    required: true,
                    fullWidth: true,
                    disabled,
                },
                }}
            />
            {errorMessages.endDate && <div className="text-danger">{errorMessages.endDate}</div>}
        </div>
    </div>
    {kind === 'fesr' 
        ? <div className='row' style={{justifyContent: 'flex-end'}}>
        <div className='col col-sm'>
            <label>Data Comunicazione Concessione</label>
            <DatePicker
                value={concessionDate ? dayjs(concessionDate) : null}
                onChange={(newValue) => {
                setConcessionDate(newValue ? newValue.toDate() : undefined);
                handleEdit("concessionDate");
                }}
                format="DD-MM-YYYY"
                slotProps={{
                textField: {
                    required: true,
                    fullWidth: true,
                    disabled,
                },
                }}
            />
            {errorMessages.concessionDate && <div className="text-danger">{errorMessages.concessionDate}</div>}
        </div>
      </div>
      : <div className='row' style={{justifyContent: 'flex-end'}}>
      <div className='col col-sm'>
          <label>Data Comunicazione CUP</label>
          <DatePicker
              value={cupCommunicationDate ? dayjs(cupCommunicationDate) : null}
              onChange={(newValue) => {
              setCupCommunicationDate(newValue ? newValue.toDate() : undefined);
              handleEdit("cupCommunicationDate");
              }}
              format="DD-MM-YYYY"
              slotProps={{
              textField: {
                  required: true,
                  fullWidth: true,
                  disabled,
              },
              }}
          />
          {errorMessages.cupCommunicationDate && <div className="text-danger">{errorMessages.cupCommunicationDate}</div>}
      </div>
  </div>
    }

    <div className='row'>
        <div className='row' style={{width:'50%'}}>
            <div className="col col-md">
                <label>Spesa Totale Ammessa</label>
                <input
                    type="text"
                    value={
                        totalRequestAllowed !== undefined
                        ? totalRequestAllowed.toLocaleString("it-IT", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                            })
                        : ""
                    }
                    onChange={(e) => {formatValue(e.target); handleEdit("totalRequestAllowed");}}
                    disabled={disabled}
                    placeholder="0,00"
                    className={errorMessages.totalRequestAllowed ? "is-invalid" : ""}
                />
                {errorMessages.totalRequestAllowed && <div className="text-danger">{errorMessages.totalRequestAllowed}</div>}
            </div>


            <div className="col col-md">
                <label>Misura Incentivo (%)</label>
                <input
                    type="number"
                    min={0}
                    max={100}
                    value={incentiveMeasure}
                    onChange={(e) => {
                        const val = Math.min(100, Math.max(0, Number(e.target.value)));
                        setIncentiveMeasure(val);
                        handleEdit("incentiveMeasure")
                    }}
                    disabled={disabled}
                    placeholder="70"
                />
            </div>
        </div>

        <div className="col col-sm" style={{alignSelf: 'end'}}>
            <FormControl style={{minWidth: '100%'}}>
                <InputLabel id="tranche-label">Tranche</InputLabel>
                <Select
                    labelId="tranche-label"
                    id="tranche-select"
                    value={tranche}
                    label="Tranche"
                    onChange={(e) => {
                        setTranche(e.target.value as 'unique' | 'first' | 'second' | 'third' | 'payoff');
                    }}
                    disabled={disabled}
                >
                    <MenuItem key='unique' value="unique">Unica</MenuItem>
                    <MenuItem key='first' value="first">Prima</MenuItem>
                    <MenuItem key='second' value="second">Seconda</MenuItem>
                    <MenuItem key='third' value="third">Terza</MenuItem>
                    <MenuItem key='payoff' value="payoff">Saldo</MenuItem>
                </Select>
            </FormControl>
        </div>

    </div>
    
    <div className='row'>
      <div className='col col-md'>
      </div>
      {kind !== 'fesr' &&
      <div className='col' style={{fontSize: '14px', fontWeight: '600'}}>
        Ricaduta territoriale 
        <Checkbox 
          checked={territorialImpact}
          onChange={()=>{setTerritorialImpact(!territorialImpact)}}
        />
      </div>
      }
    </div>

    <div className='row'>
        <div className='col col-md'>
            <label style={{ marginTop: '20px' }}>File Allegati</label>
            <div {...getZipRootProps()} className={`dropzone ${isZipDragActive ? 'active' : ''}`}>
                <input {...getZipInputProps()} />
                {zipFile ? (
                <div className="file-preview">
                    <div className="file-info">
                        <span>{zipFile.name}</span>
                        <small>{(zipFile.size / (1024 * 1024)).toFixed(1)} MB</small>
                    </div>
                </div>
                ) : (
                <p>{isZipDragActive ? 'Rilascia il file ZIP qui...' : 'Trascina il file ZIP qui o clicca per selezionare'}</p>
                )}
            </div>
        </div>
        { kind === 'fesr' &&
        <div className='col col-md'>
            <label style={{ marginTop: '20px' }}>File Export Imputazioni</label>
            <div {...getExcelRootProps()} className={`dropzone ${isExcelDragActive ? 'active' : ''}`}>
                <input {...getExcelInputProps()} />
                {excelFile ? (
                <div className="file-preview">
                    <div className="file-info">
                        <span>{excelFile.name}</span>
                        <small>{(excelFile.size / 1024).toFixed(1)} KB</small>
                    </div>
                </div>
                ) : (
                <p>{isExcelDragActive ? 'Rilascia il file qui...' : 'Trascina il file Excel o CSV qui o clicca per selezionare'}</p>
                )}
            </div>
        </div>
        }

    </div>

    </>
  );
};

export default CustomForm;
